(() => {
  document.documentElement.classList.add('p11-plan-ready');

  const isValidUsDate = (value) => {
    const match = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/([0-9]{4})$/.exec(value);
    if (!match) return false;

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(0);
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCFullYear(year, month - 1, day);
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  };

  const forms = [...document.querySelectorAll('.p11-plan-form')];

  forms.forEach((form) => {
    const formatField = form.querySelector('[data-p11-plan-format-field]');
    const formatTrigger = form.querySelector('[data-p11-plan-format-trigger]');
    const formatMenu = form.querySelector('[data-p11-plan-format-menu]');
    const formatSummary = form.querySelector('[data-p11-plan-format-summary]');
    const formatOutput = form.querySelector('[data-p11-plan-format-output]');
    const formatFallback = form.querySelector('[data-p11-plan-format-fallback]');
    const formatOptions = [...form.querySelectorAll('[data-p11-plan-format-option]')];
    const timing = form.querySelector('[data-p11-plan-timing]');
    const targetDateField = form.querySelector('[data-p11-plan-target-date]');
    const targetDate = targetDateField?.querySelector('input');
    const notes = form.querySelector('textarea[maxlength="2000"]');
    const counter = form.querySelector('[data-p11-plan-counter]');
    const submitButton = form.querySelector('[data-p11-plan-submit]');
    const submitLabel = form.querySelector('[data-p11-plan-submit-label]');
    const errorSummary = form.querySelector('[data-p11-plan-error-summary]');
    const errorList = form.querySelector('[data-p11-plan-error-list]');

    if (submitButton && submitLabel) submitButton.dataset.defaultLabel = submitLabel.textContent.trim();

    const setFormatMenuState = (open) => {
      if (!formatField || !formatTrigger || !formatMenu) return;
      formatField.classList.toggle('is-open', open);
      formatTrigger.setAttribute('aria-expanded', String(open));
      formatMenu.hidden = !open;
    };

    const selectedFormats = () => formatOptions.filter((option) => option.checked);

    const updateFormatSummary = () => {
      const selected = selectedFormats();
      const labels = selected.map((option) => option.dataset.label || option.value);
      if (formatOutput) formatOutput.value = labels.join(', ');
      if (!formatSummary) return;

      formatSummary.classList.toggle('is-placeholder', selected.length === 0);
      if (!selected.length) {
        formatSummary.textContent = formatSummary.dataset.placeholder || 'Select packaging formats';
      } else if (selected.length === 1) {
        formatSummary.textContent = labels[0];
      } else {
        formatSummary.textContent = `${selected.length} packaging formats selected`;
      }
    };

    const updateFormatSelection = (changedOption) => {
      const uncertain = formatOptions.find((option) => option.hasAttribute('data-uncertain'));
      if (changedOption?.hasAttribute('data-uncertain') && changedOption.checked) {
        formatOptions.forEach((option) => {
          if (option !== changedOption) option.checked = false;
        });
      } else if (changedOption?.checked && uncertain) {
        uncertain.checked = false;
      }
      updateFormatSummary();
    };

    const updateTiming = () => {
      if (!targetDateField || !timing) return;
      const show = timing.selectedOptions[0]?.hasAttribute('data-p11-plan-target-date-option') || false;
      targetDateField.hidden = !show;
      targetDateField.closest('.p11-plan-timing-combo')?.classList.toggle('is-date-visible', show);
      if (targetDate) {
        targetDate.disabled = !show;
        targetDate.required = show;
      }
    };

    const updateCounter = () => {
      if (!notes || !counter) return;
      counter.textContent = `${notes.value.length.toLocaleString('en-US')} / 2,000`;
    };

    const updateSelectPlaceholder = (select) => select.classList.toggle('is-placeholder', !select.value);

    const clearErrors = () => {
      form.querySelectorAll('.has-error').forEach((field) => field.classList.remove('has-error'));
      form.querySelectorAll('.p11-plan-field-error').forEach((error) => error.remove());
      form.querySelectorAll('[aria-invalid="true"]').forEach((control) => {
        control.removeAttribute('aria-invalid');
        const ids = (control.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter((id) => id && !id.endsWith('-client-error'));
        if (ids.length) control.setAttribute('aria-describedby', ids.join(' '));
        else control.removeAttribute('aria-describedby');
      });
      if (errorSummary) errorSummary.hidden = true;
      if (errorList) errorList.innerHTML = '';
    };

    const addError = (container, control, message, index) => {
      if (!container || !control) return null;
      if (!container.id) container.id = `${form.id}-required-${index}`;
      if (!control.id) control.id = `${container.id}-control`;

      const errorId = `${container.id}-client-error`;
      container.classList.add('has-error');
      control.setAttribute('aria-invalid', 'true');
      const describedBy = control.getAttribute('aria-describedby');
      control.setAttribute('aria-describedby', [describedBy, errorId].filter(Boolean).join(' '));

      const error = document.createElement('p');
      error.className = 'p11-plan-field-error';
      error.id = errorId;
      error.textContent = message;
      container.append(error);
      return { id: control.id, message };
    };

    const validate = () => {
      clearErrors();
      const errors = [];
      const requiredControls = [...form.querySelectorAll('input[required], select[required], textarea[required]')]
        .filter((control) => !control.disabled);

      requiredControls.forEach((control, index) => {
        const value = String(control.value || '').trim();
        let message = '';
        if (!value) {
          const fieldName = control.closest('[data-p11-plan-field]')?.dataset.p11PlanField;
          message = {
            name: 'Enter your name.',
            email: 'Enter your email address.',
            phone: 'Enter your phone number.',
            country: 'Select a delivery country or region.',
          }[fieldName] || 'Complete this field.';
        } else if (control.type === 'email' && !control.validity.valid) {
          message = 'Enter a valid email address.';
        } else if (control === targetDate && !isValidUsDate(value)) {
          message = 'Enter a valid date as MM/DD/YYYY.';
        }

        if (message) errors.push(addError(control.closest('.p11-plan-field'), control, message, index + 1));
      });

      if (!selectedFormats().length) {
        errors.push(addError(formatField, formatTrigger, 'Select at least one packaging format.', requiredControls.length + 1));
      }

      const validErrors = errors.filter(Boolean);
      if (!validErrors.length) return true;

      validErrors.forEach((error) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${error.id}`;
        link.textContent = error.message;
        item.append(link);
        errorList?.append(item);
      });

      if (errorSummary) {
        errorSummary.hidden = false;
        errorSummary.focus();
      }
      return false;
    };

    formatTrigger?.addEventListener('click', () => {
      setFormatMenuState(formatTrigger.getAttribute('aria-expanded') !== 'true');
    });
    formatOptions.forEach((option) => option.addEventListener('change', () => updateFormatSelection(option)));
    timing?.addEventListener('change', updateTiming);
    notes?.addEventListener('input', updateCounter);
    form.querySelectorAll('[data-p11-plan-select]').forEach((select) => {
      updateSelectPlaceholder(select);
      select.addEventListener('change', () => updateSelectPlaceholder(select));
    });

    document.addEventListener('click', (event) => {
      if (!formatField?.contains(event.target)) setFormatMenuState(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || formatTrigger?.getAttribute('aria-expanded') !== 'true') return;
      setFormatMenuState(false);
      formatTrigger.focus();
    });

    form.addEventListener('submit', (event) => {
      updateFormatSummary();
      if (!validate()) {
        event.preventDefault();
        return;
      }
      if (submitButton) submitButton.disabled = true;
      if (submitLabel && submitButton?.dataset.loadingLabel) submitLabel.textContent = submitButton.dataset.loadingLabel;
    });

    setFormatMenuState(false);
    updateFormatSummary();
    updateTiming();
    updateCounter();

    if (formatOutput) formatOutput.disabled = false;
    formatTrigger?.removeAttribute('hidden');
    if (formatFallback) {
      formatFallback.querySelector('select')?.setAttribute('disabled', 'disabled');
      formatFallback.hidden = true;
    }
    form.noValidate = true;
  });

  document.querySelector('[data-p11-plan-server-error]')?.focus();
  document.querySelector('[data-p11-plan-success]')?.focus();

  document.querySelectorAll('[data-p11-plan-focus-form]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      window.setTimeout(() => document.querySelector('.p11-plan-form input[autocomplete="name"]')?.focus(), 450);
    });
  });

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-p11-plan-submit]').forEach((button) => {
      button.disabled = false;
      const label = button.querySelector('[data-p11-plan-submit-label]');
      if (label && button.dataset.defaultLabel) label.textContent = button.dataset.defaultLabel;
    });
  });
})();
